import useDataProtectionHook from "@/hooks/use-data-protection.hook.ts";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import {
  useDataProtectionDeleteRequest,
  useDataProtectionEditRequest,
} from "@/queries/use-data-protection.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiDataProtectionResponsePayload,
  DataProtectionResponsePage,
} from "@/types/data-protection.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Stack, Text, Textarea, Title } from "@skailer-com/uikit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { z } from "zod";

export interface DataProtectionResponseFormProps {
  page: DataProtectionResponsePage;
  onSent: () => void;
}

export function DataProtectionResponseForm({ page, onSent }: DataProtectionResponseFormProps) {
  const { getDataForResponsePage } = useDataProtectionHook();
  const pageData = getDataForResponsePage(page);
  const { t } = useTranslation();
  const { uuid, object_id } = useParams();

  const editRequest = useDataProtectionEditRequest();
  const deleteRequest = useDataProtectionDeleteRequest();
  const DataProtectionAnswerSchemaObject = z.object({
    comment: z.string().optional(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(DataProtectionAnswerSchemaObject),
    defaultValues: {
      comment: "",
    },
  });

  const onSubmit = handleSubmit((data) => {
    const payload: ApiDataProtectionResponsePayload = {
      object_id: object_id ?? "",
      uuid: uuid ?? "",
      comment: data.comment ?? "",
    };

    switch (page) {
      case DataProtectionResponsePage.editRequest:
        editRequest.mutate(payload);
        break;
      case DataProtectionResponsePage.deleteRequest:
        deleteRequest.mutate(payload);
        break;
    }

    onSent();

    analyticsService.trackEvent(analyticEvents.dataProtection.responseReceived, {
      [analyticProperties.actionType]: pageData.analyticsPage,
      [analyticProperties.id]: object_id,
    });
  });

  const isPending = editRequest.isPending || deleteRequest.isPending;

  return (
    <form className="" onSubmit={onSubmit}>
      <Stack gap={"xl"}>
        <Stack gap={"sm"}>
          <Title header={pageData.title} />
          <Text>{pageData.description}</Text>
        </Stack>
        <Textarea
          label={t("common:message")}
          {...register("comment")}
          error={errors.comment?.message}
        />
        <ServerErrorField errors={errors} />
        <Button
          type={"submit"}
          size="lg"
          onClick={onSubmit}
          className="w-full"
          isLoading={isPending}
        >
          {pageData.btn}
        </Button>
      </Stack>
    </form>
  );
}
